import { useMutation, useQuery, UseQueryResult } from '@tanstack/react-query';

import {
  forgotPassword,
  getGuest,
  getUser,
  login,
  recoverPassword,
  register,
  verifyEmail,
} from '@/api/auth/auth';
import { IGuest, IUser } from '@/api/auth/types';
import { useAuth } from '@/hooks/useAuth';

export function useLogin() {
  const { loginInStore } = useAuth();

  return useMutation({
    mutationFn: login,
    onSuccess: async (data) => {
      if (data.access_token) {
        loginInStore(data.access_token, data.refresh_token);
      }
    },
  });
}

export function useRegister() {
  return useMutation({
    mutationFn: register,
  });
}

export function useVerifyEmail(token: string | undefined) {
  return useQuery({
    queryKey: ['verifyEmail', token],
    queryFn: () => verifyEmail(token as string),
    enabled: !!token,
    retry: false,
  });
}

export function useForgotPassword() {
  return useMutation({
    mutationFn: forgotPassword,
  });
}

export function useRecoverPassword() {
  return useMutation({
    mutationFn: recoverPassword,
  });
}

export function useUser(): UseQueryResult<IUser, Error> {
  const { isLogin } = useAuth();

  return useQuery<IUser, Error>({
    queryKey: ['me'],
    queryFn: getUser,
    enabled: isLogin,
    refetchOnMount: true,
    retry: false,
  });
}

export function useGuest(): UseQueryResult<IGuest, Error> {
  const { isLogin, isInitialized } = useAuth();

  return useQuery({
    queryKey: ['guest'],
    queryFn: getGuest,
    enabled: !isLogin && isInitialized,
    refetchOnMount: true,
  });
}
